import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {RouterModule} from '@angular/router';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ApiAgentService} from '../../../services/api-agent.service';
import {GlobalLoaderService} from '../../../services/global-loader.service';
import {WithdrawRequest} from '../../../models/withdraw-request.model';
import {firstValueFrom} from 'rxjs';

declare var Snackbar: any;

@Component({
  selector: 'app-withdraw-history',
  templateUrl: './withdraw-history.component.html',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  styleUrls: ['./withdraw-history.component.scss']
})
export class WithdrawHistoryComponent implements OnInit {
  withdrawRequests: WithdrawRequest[] = [];
  loading = true;

  // Pagination properties
  currentPage = 0;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;

  constructor(
    private api: ApiAgentService,
    private loader: GlobalLoaderService,
    private translate: TranslateService
  ) {}

  async ngOnInit() {
    this.loading = true;
    await this.loadResources();
    await this.loadWithdrawRequests();
    this.loading = false;
  }

  async loadResources() {
    await this.loader.loadExternalStyles('/plugins/notification/snackbar/snackbar.min.css');
    await this.loader.loadExternalScript('/plugins/notification/snackbar/snackbar.min.js');
  }

  async loadWithdrawRequests(page = 0) {
    this.loading = true;
    try {
      const response = await this.api.getAffiliateWithdrawRequests(page, this.pageSize);

      // If API returns paginated data with content/pagination metadata
      if (response && response.content) {
        this.withdrawRequests = response.content;
        this.totalItems = response.totalElements;
        this.totalPages = response.totalPages;
        this.currentPage = response.number;
      }
      // If API returns data wrapped in a property
      else if (response && response.withdrawRequests) {
        // If backend doesn't support pagination yet, we'll handle it client-side
        const allRequests = response.withdrawRequests;
        this.totalItems = allRequests.length;
        this.totalPages = Math.ceil(this.totalItems / this.pageSize);

        // Get just the current page of items
        const startIndex = page * this.pageSize;
        this.withdrawRequests = allRequests.slice(startIndex, startIndex + this.pageSize);
        this.currentPage = page;
      }
      // Direct array response
      else if (Array.isArray(response)) {
        const allRequests = response;
        this.totalItems = allRequests.length;
        this.totalPages = Math.ceil(this.totalItems / this.pageSize);

        // Get just the current page of items
        const startIndex = page * this.pageSize;
        this.withdrawRequests = allRequests.slice(startIndex, startIndex + this.pageSize);
        this.currentPage = page;
      }
      // Unexpected response format
      else {
        console.error('Unexpected response format:', response);
        this.withdrawRequests = [];
        this.totalItems = 0;
        this.totalPages = 0;
      }

      // Sort requests by date (newest first)
      this.withdrawRequests.sort((a, b) => {
        return new Date(b.requestDate).getTime() - new Date(a.requestDate).getTime();
      });
    } catch (error) {
      console.error('Error loading withdraw requests:', error);
      this.showToast(await firstValueFrom(this.translate.get('Error loading withdraw requests')));
      this.withdrawRequests = [];
      this.totalItems = 0;
      this.totalPages = 0;
    } finally {
      this.loading = false;
    }
  }

  changePage(page: number) {
    if (page >= 0 && page < this.totalPages) {
      this.loadWithdrawRequests(page);
    }
  }

  private showToast(msg: string) {
    Snackbar.show({
      text: msg,
      pos: 'bottom-center',
      duration: 3000
    });
  }

  getStatusClass(status: string): string {
    if (!status) return 'badge badge-secondary';

    switch (status.toLowerCase()) {
      case 'approved':
        return 'badge badge-success';
      case 'rejected':
      case 'denied':
        return 'badge badge-danger';
      case 'pending':
        return 'badge badge-warning';
      case 'processing':
        return 'badge badge-info';
      default:
        return 'badge badge-secondary';
    }
  }
}
